// src/App.tsx - Updated with Admin-Only Routing
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Backdrop } from '@mui/material';
import { lightTheme, darkTheme } from './styles/theme';

// Regular User Pages
import Home from './pages/Home';
import IndustryPage from './pages/IndustryPage';
import SubIndustryPage from './pages/SubIndustryPage';
import ValueChainPage from './pages/ValueChainPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ArticlePage from './pages/ArticlePage';
import ArticlesListPage from './pages/ArticlesListPage';
import ExpertsPage from './pages/ExpertsPage';
import ExpertDetail from './pages/ExpertDetail';
import ProfilePage from './pages/ProfilePage';
import MarketplacePage from './pages/MarketplacePage';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import InvestorRequestDetailPage from './pages/InvestorRequestDetailPage';
import SubmitOpportunityForm from './pages/SubmitOpportunityForm';
import SubmitInvestorRequestForm from './pages/SubmitInvestorRequestForm';
import MyApplicationsPage from './pages/MyApplicationsPage';
import MembersPage from './pages/MembersPage';
import MemberDetailPage from './pages/MemberDetailPage';
import ConnectionsManagement from './pages/ConnectionsManagement';
import MessagesPage from './pages/MessagesPage';
import AllIndustriesPage from './pages/AllIndustriesPage';
import IndustriesAnalyticsPage from './pages/IndustriesAnalyticsPage';
import IndustriesReportPage from './pages/IndustriesReportPage';
import IndustriesReportPageNew from './pages/IndustriesReportPageNew';
import SupportEcosystemDetailPage from './pages/SupportEcosystemDetailPage';

// Admin Pages
import AdminDashboard from './components/admin/adminDashboard';
import AdminUserManagement from './components/admin/UserManagement';
import AdminIndustryManagement from './components/admin/IndustryManagement';
import MarketplaceManagement from './components/admin/MarketplaceManagement';
import ContentManagement from './components/admin/ContentManagement';
import SystemSettings from './components/admin/SystemSettings';
import IndustryDetailsPage from './components/admin/IndustryDetailsPage';
import SubIndustryDetailsPage from './components/admin/SubIndustryDetailsPage';
import TestimonialsManagement from './pages/admin/TestimonialsManagement';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminReports from './pages/admin/AdminReports';
import AdminSupport from './pages/admin/AdminSupport';
import AdminManagement from './pages/admin/AdminManagement';
import HeroSectionManagement from './components/admin/HeroSectionManagement';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminSettings from './pages/admin/AdminSettings';
import SupportEcosystemManagement from './components/admin/SupportEcosystemManagement';

// Expert Pages
import ExpertArticles from './pages/ExpertArticles';

// Common Components
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Footer from './components/common/Footer';

// Admin-specific Components
import AdminHeader from './components/admin/AdminHeader';
import AdminSidebar from './components/admin/AdminSidebar';

// Auth Components
import { useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";

function App() {
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auth from context
  const { isAuthenticated, user, login, logout } = useAuth();

  // Map backend user -> components that expect `role`
  const mappedUser = user
    ? {
        ...user,
        role: user.userType, // 'member' | 'expert' | 'admin'
        email: user.email || '', // Ensure email is always a string
      }
    : null;

  const theme = themeMode === 'dark' ? darkTheme : lightTheme;

  const toggleTheme = () => setThemeMode((m) => (m === 'dark' ? 'light' : 'dark'));
  const toggleSidebar = () => setSidebarOpen((o) => !o);
  const closeSidebar = () => setSidebarOpen(false);

  // Persist theme
  useEffect(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'light' || saved === 'dark') setThemeMode(saved);
  }, []);
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  // Check if user is admin
  const isAdmin = user?.userType === 'admin';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
          
          {/* Conditional Header - Admin vs Regular */}
          {isAdmin ? (
            <AdminHeader
              toggleTheme={toggleTheme}
              toggleSidebar={toggleSidebar}
              currentTheme={themeMode}
              isAuthenticated={isAuthenticated}
              user={mappedUser}
              onLogout={logout}
            />
          ) : (
            <Header
              toggleTheme={toggleTheme}
              toggleSidebar={toggleSidebar}
              currentTheme={themeMode}
              isAuthenticated={isAuthenticated}
              user={mappedUser}
              onLogout={logout}
            />
          )}

          <Box sx={{ display: 'flex', flex: 1, position: 'relative' }}>
            
            {/* Conditional Sidebar - Admin vs Regular */}
            {isAdmin ? (
              <AdminSidebar
                open={sidebarOpen}
                onClose={closeSidebar}
                isAuthenticated={isAuthenticated}
                user={mappedUser}
              />
            ) : (
              <Sidebar
                open={sidebarOpen}
                onClose={closeSidebar}
                isAuthenticated={isAuthenticated}
                user={mappedUser}
              />
            )}

            {sidebarOpen && (
              <Backdrop
                open={sidebarOpen}
                onClick={closeSidebar}
                sx={{
                  zIndex: (t) => t.zIndex.drawer - 1,
                  backgroundColor: 'rgba(0,0,0,0.2)'
                }}
              />
            )}

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                width: '100%',
                mt: { xs: 7, sm: 8 },
                p: { xs: 2, sm: 3 },
                transition: 'all 0.3s',
                overflow: 'hidden'
              }}
            >
              <Routes>
                {/* Root redirect - Split by user type */}
                <Route 
                  path="/" 
                  element={
                    !isAuthenticated ? (
                      <Navigate to="/login" />
                    ) : isAdmin ? (
                      <Navigate to="/admin/dashboard" />
                    ) : (
                      <Navigate to="/home" />
                    )
                  } 
                />

                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    isAuthenticated
                      ? isAdmin 
                        ? <Navigate to="/admin/dashboard" />
                        : <Navigate to="/home" />
                      : <LoginPage onLogin={login} isAuthenticated={isAuthenticated} />
                  }
                />

                <Route
                  path="/register"
                  element={
                    isAuthenticated
                      ? isAdmin
                        ? <Navigate to="/admin/dashboard" />
                        : <Navigate to="/home" />
                      : <RegisterPage />
                  }
                />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Admin Routes - Only accessible by admins */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminProtectedRoute>
                      <AdminDashboard />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminProtectedRoute>
                      <AdminUserManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/industries"
                  element={
                    <AdminProtectedRoute>
                      <AdminIndustryManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/industry-details/:id"
                  element={
                    <AdminProtectedRoute>
                      <IndustryDetailsPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/industries/:industryId/subindustry/:subIndustryId"
                  element={
                    <AdminProtectedRoute>
                      <SubIndustryDetailsPage />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/marketplace"
                  element={
                    <AdminProtectedRoute>
                      <MarketplaceManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/content"
                  element={
                    <AdminProtectedRoute>
                      <ContentManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <AdminProtectedRoute>
                      <AdminSettings />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/notifications"
                  element={
                    <AdminProtectedRoute>
                      <AdminNotifications />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/security"
                  element={
                    <AdminProtectedRoute>
                      <AdminSecurity />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/testimonials"
                  element={
                    <AdminProtectedRoute>
                      <TestimonialsManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/hero-section"
                  element={
                    <AdminProtectedRoute>
                      <HeroSectionManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <AdminProtectedRoute>
                      <AdminAnalytics />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/reports"
                  element={
                    <AdminProtectedRoute>
                      <AdminReports />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/support"
                  element={
                    <AdminProtectedRoute>
                      <AdminSupport />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/support/:userId"
                  element={
                    <AdminProtectedRoute>
                      <AdminSupport />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/admin-management"
                  element={
                    <AdminProtectedRoute>
                      <AdminManagement />
                    </AdminProtectedRoute>
                  }
                />
                <Route
                  path="/admin/support-ecosystem"
                  element={
                    <AdminProtectedRoute>
                      <SupportEcosystemManagement />
                    </AdminProtectedRoute>
                  }
                />

                {/* Expert Routes */}
                <Route
                  path="/expert/articles"
                  element={
                    <ProtectedRoute>
                      <ExpertArticles />
                    </ProtectedRoute>
                  }
                />
                {/* Redirect /admin to dashboard */}
                <Route
                  path="/admin"
                  element={
                    <AdminProtectedRoute>
                      <Navigate to="/admin/dashboard" replace />
                    </AdminProtectedRoute>
                  }
                />

                {/* Regular User Routes - Only accessible by non-admin users */}
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <Home />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/industries"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/industries" /> : <AllIndustriesPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <IndustriesAnalyticsPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <IndustriesReportPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports-new"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <IndustriesReportPageNew />}
                    </ProtectedRoute>
                  }
                />
                
                {/* Marketplace - Regular users only */}
                <Route 
                  path="/marketplace" 
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <MarketplacePage />}
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/marketplace/opportunity/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <OpportunityDetailPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/request/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <InvestorRequestDetailPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/submit-opportunity"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <SubmitOpportunityForm />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketplace/submit-request"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <SubmitInvestorRequestForm />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-applications"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <MyApplicationsPage />}
                    </ProtectedRoute>
                  }
                />

                {/* Industry Pages - Regular users only */}
                <Route
                  path="/industry/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/industries" /> : <IndustryPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/support-ecosystem/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/industries" /> : <SupportEcosystemDetailPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/industry/:id/sub/:subId"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/industries" /> : <SubIndustryPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/industry/:id/sub/:subId/subsub/:subSubId"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/industries" /> : <SubIndustryPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/valuechain/:type/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/industries" /> : <ValueChainPage />}
                    </ProtectedRoute>
                  }
                />

                {/* Content Pages - Regular users only */}
                <Route
                  path="/articles"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/dashboard" /> : <ArticlesListPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/article/:type/:id"
                  element={<ArticlePage />}
                />
                <Route
                  path="/experts"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <ExpertsPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/expert/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <ExpertDetail />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/settings" /> : <ProfilePage />}
                    </ProtectedRoute>
                  }
                />

                {/* Members Pages - Regular users only */}
                <Route
                  path="/members"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <MembersPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/members/:segment"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <MembersPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/member/:id"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <MemberDetailPage />}
                    </ProtectedRoute>
                  }
                />

                {/* Connections and Messages Pages - Regular users only */}
                <Route
                  path="/connections"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <ConnectionsManagement />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <MessagesPage />}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages/:userId"
                  element={
                    <ProtectedRoute>
                      {isAdmin ? <Navigate to="/admin/users" /> : <MessagesPage />}
                    </ProtectedRoute>
                  }
                />

                {/* Catch-all */}
                <Route
                  path="*"
                  element={
                    !isAuthenticated ? (
                      <Navigate to="/login" />
                    ) : isAdmin ? (
                      <Navigate to="/admin/dashboard" />
                    ) : (
                      <Navigate to="/home" />
                    )
                  }
                />
              </Routes>
            </Box>
          </Box>

          <Footer />
        </Box>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;